import { moveToFulfilled, moveToPendingIfNotFulfilled, update } from '@ms/yammer-data-support';
import { GroupMembershipStatus, GroupPrivacy, GroupState } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type {
  AllNetworkGroupsLoadFulfilledAction,
  AllNetworkGroupsLoadMoreFulfilledAction,
} from '../../domains/allNetworkGroups/actions.types';
import type { FirstRunSuggestedGroupsLoadFulfilledAction } from '../../domains/firstRunSuggestedGroups/actions.types';
import type { DeleteGroupFulfilledAction } from '../../domains/groupDeletion/actions.types';
import type { GroupEventsSidebarLoadFulfilledAction } from '../../domains/groupEventsSidebarLoad/actions.types';
import type {
  GroupHeaderLoadFulfilledAction,
  GroupHeaderLoadPendingAction,
} from '../../domains/groupHeaderLoad/actions.types';
import type {
  GroupJoinedFulfilledAction,
  GroupJoinedUpdaterAction,
  GroupLeftFulfilledAction,
} from '../../domains/groupJoinLeave/actions.types';
import type {
  GuestUserAcceptGroupInvitationFulfilledAction,
  GuestUserAcceptGroupInvitationUpdaterAction,
} from '../../domains/groupJoinLeaveWhenInvited/actions.types';
import type { GroupRelatedGroupsLoadFulfilledAction } from '../../domains/groupRelatedGroupsLoad/actions.types';
import type { GroupCreateFulfilledAction, GroupEditFulfilledAction } from '../../domains/groups/actions.types';
import type { GroupMarkAsMultiTenantOrganizationGroupFulfilledAction } from '../../domains/multiTenantOrganizationGroup/markAsMultiTenantOrganizationGroupActions.types';
import type {
  MyGroupsLoadFulfilledAction,
  MyGroupsLoadMoreFulfilledAction,
} from '../../domains/myGroups/actions.types';
import type { NavigationGroupsLoadFulfilledAction } from '../../domains/navigationGroupsLoad/actions.types';
import type {
  OfficialGroupsLoadFulfilledAction,
  OfficialGroupsLoadMoreFulfilledAction,
} from '../../domains/officialGroups/actions.types';
import type {
  RecommendedGroupsLoadFulfilledAction,
  RecommendedGroupsLoadMoreFulfilledAction,
} from '../../domains/recommendedGroups/actions.types';
import type {
  SuggestedGroupsLoadFulfilledAction,
  SuggestedGroupsLoadMoreFulfilledAction,
} from '../../domains/suggestedGroups/actions.types';
import type { UserSidebarLoadFulfilledAction } from '../../domains/userSidebarLoad/actions.types';
import type {
  ViewerCommunityRelationshipLoadFulfilledAction,
  ViewerCommunityRelationshipLoadPendingAction,
} from '../../domains/viewerCommunityRelationshipLoad/actions.types';
import type {
  AddFavoriteCommunityPendingAction,
  AddFavoriteCommunityRejectedAction,
  RemoveFavoriteCommunityPendingAction,
  RemoveFavoriteCommunityRejectedAction,
} from '../../domains/viewerCommunityRelationships/actions.types';
import type { ViewerCommunityRelationships } from '../types';

export type ViewerCommunityRelationshipAction =
  | AddFavoriteCommunityPendingAction
  | AddFavoriteCommunityRejectedAction
  | GroupCreateFulfilledAction
  | DeleteGroupFulfilledAction
  | GroupEditFulfilledAction
  | GroupEventsSidebarLoadFulfilledAction
  | GroupHeaderLoadFulfilledAction
  | GroupHeaderLoadPendingAction
  | GroupJoinedFulfilledAction
  | GroupJoinedUpdaterAction
  | GroupLeftFulfilledAction
  | GuestUserAcceptGroupInvitationFulfilledAction
  | GuestUserAcceptGroupInvitationUpdaterAction
  | NavigationGroupsLoadFulfilledAction
  | RemoveFavoriteCommunityPendingAction
  | RemoveFavoriteCommunityRejectedAction
  | ViewerCommunityRelationshipLoadPendingAction
  | ViewerCommunityRelationshipLoadFulfilledAction
  | RecommendedGroupsLoadFulfilledAction
  | RecommendedGroupsLoadMoreFulfilledAction
  | FirstRunSuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadFulfilledAction
  | SuggestedGroupsLoadMoreFulfilledAction
  | UserSidebarLoadFulfilledAction
  | AllNetworkGroupsLoadFulfilledAction
  | AllNetworkGroupsLoadMoreFulfilledAction
  | MyGroupsLoadFulfilledAction
  | MyGroupsLoadMoreFulfilledAction
  | OfficialGroupsLoadFulfilledAction
  | OfficialGroupsLoadMoreFulfilledAction
  | GroupRelatedGroupsLoadFulfilledAction
  | GroupMarkAsMultiTenantOrganizationGroupFulfilledAction;

const initialState: ViewerCommunityRelationships = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const reducer: Reducer<ViewerCommunityRelationships, ViewerCommunityRelationshipAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case 'COMMUNITY.ADD_FAVORITE_PENDING':
      return update(state, action.payload.id, { isFavorite: true });
    case 'COMMUNITY.ADD_FAVORITE_REJECTED':
      return update(state, action.payload.id, { isFavorite: false });
    case 'COMMUNITY.REMOVE_FAVORITE_PENDING':
      return update(state, action.payload.id, { isFavorite: false });
    case 'COMMUNITY.REMOVE_FAVORITE_REJECTED':
      return update(state, action.payload.id, { isFavorite: true });
    case 'GROUP.EDIT_FULFILLED':
      return update(state, action.payload.groupId, {
        privacy: action.payload.entities.viewerCommunityRelationships[action.payload.groupId].privacy,
        threadstarterRestricted:
          action.payload.entities.viewerCommunityRelationships[action.payload.groupId].threadstarterRestricted,
      });
    case 'GROUP_EVENTS_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'GROUP_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'GROUP_HEADER.LOAD_PENDING':
      return moveToPendingIfNotFulfilled(state, action.payload.groupId);
    case 'GROUP.JOINED_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'GROUP.JOINED_UPDATER':
      return update(state, action.payload.groupId, {
        membershipStatus: action.payload.membershipStatus as GroupMembershipStatus,
        canStartThread: action.payload.canStartThread,
        state: action.payload.state as GroupState,
      });
    case 'GROUP.LEFT_FULFILLED':
      return update(state, action.payload.groupId, { membershipStatus: GroupMembershipStatus.NONE, isFavorite: false });
    case 'GROUP.GUEST_INVITE_ACCEPT_REQUEST_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'GROUP.GUEST_INVITE_ACCEPT_REQUEST_UPDATER':
      return update(state, action.payload.groupId, {
        membershipStatus: action.payload.membershipStatus as GroupMembershipStatus,
        canStartThread: action.payload.canStartThread,
        state: action.payload.state as GroupState,
      });
    case 'NAVIGATION_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'ALL_NETWORK_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'ALL_NETWORK_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'RECOMMENDED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'RECOMMENDED_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'FRE_SUGGESTED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'SUGGESTED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'SUGGESTED_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'USER_SIDEBAR.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'VIEWER_COMMUNITY_RELATIONSHIP.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'VIEWER_COMMUNITY_RELATIONSHIP.LOAD_PENDING':
      return moveToPendingIfNotFulfilled(state, action.payload.communityId);
    case 'GROUP.DELETE_FULFILLED':
      return update(state, action.payload.groupId, { state: GroupState.DELETED });
    case 'MY_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'OFFICIAL_GROUPS.LOAD_FULFILLED':
    case 'OFFICIAL_GROUPS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'GROUP_RELATED_GROUPS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.viewerCommunityRelationships);
    case 'GROUP.MARK_AS_MTO_GROUP_FULFILLED':
      return update(state, action.payload.groupId, { privacy: GroupPrivacy.PRIVATE });
    default:
      return state;
  }
};

export default reducer;
